import type { ModalRegistry } from '@rpgc-ai/services/modal/modal-registry.token';

import { AGE_CONFIRMATION_ACCEPTED_RESULT, MODAL_PRIORITY } from '@rpgc-ai/shared-interfaces/consts/modal-priority.const';
import { EModalName } from '@rpgc-ai/shared-interfaces/enums/modal-name.enum';
import { EConstructorInitiator } from '@rpgc-ai/shared-interfaces/enums/preset-constructor.enum';
import { EChatbotCreateStatus } from '@rpgc-ai/shared-interfaces/interfaces/chatbot-create.interface';

import { EModalType } from '@rpgc-ai/lazy-blocks/complain/complain.component';

export const modalRegistry: ModalRegistry = {
  // Template modal (openModalWithTemplate) — no explicit entry needed, handled by openTemplate()

  // Context menu
  'context-menu': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/mobile-context-menu/mobile-context-menu.component').then((m) => m.MobileContextMenuComponent),
  },

  // Languages
  languages: {
    component: () => import('@rpgc-ai/lazy-blocks/languages-modal/languages-modal.component').then((m) => m.LanguagesModalComponent),
    options: { title: 'LANGUAGES.TITLE' },
  },

  // Auth
  [EModalName.SIGN_IN]: {
    component: () => import('@rpgc-ai/lazy-blocks/login-providers/login-providers.component').then((m) => m.LoginProvidersComponent),
  },
  'sign-up': {
    component: () => import('@rpgc-ai/lazy-blocks/registration/registration.component').then((m) => m.RegistrationComponent),
    options: { title: 'MODALS.REGISTRATION.TITLE' },
  },
  'password-recovery': {
    component: () => import('@rpgc-ai/lazy-blocks/password-recovery/password-recovery.component').then((m) => m.PasswordRecoveryComponent),
    options: { title: 'MODALS.PASSWORD_RECOVERY.TITLE' },
  },
  'password-reset': {
    component: () => import('@rpgc-ai/lazy-blocks/password-reset/password-reset.component').then((m) => m.PasswordResetComponent),
    options: { title: 'MODALS.PASSWORD_RECOVERY.RESET_PASSWORD' },
  },
  'login-email': {
    component: () => import('@rpgc-ai/lazy-blocks/login-email/login-email.component').then((m) => m.LoginEmailComponent),
  },
  'magic-link': {
    component: () => import('@rpgc-ai/lazy-blocks/modal-magic-link/modal-magic-link.component').then((m) => m.ModalMagicLinkComponent),
  },
  'login-two-factor': {
    component: () => import('@rpgc-ai/lazy-blocks/login-two-factor/login-two-factor.component').then((m) => m.LoginTwoFactorComponent),
  },

  // NSFW
  'allow-nsfw-content': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/allow-nsfw-content/allow-nsfw-content.component').then((m) => m.AllowNsfwContentComponent),
  },

  // Image generation
  'generate-avatar': {
    component: () => import('@rpgc-ai/lazy-blocks/generate-avatar/generate-avatar.component').then((m) => m.GenerateAvatarComponent),
  },
  'generate-background': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/generate-background/generate-background.component').then((m) => m.GenerateBackgroundComponent),
  },
  'image-generation-settings': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/image-generation-settings/image-generation-settings.component').then(
        (m) => m.ImageGenerationsSettingsComponent,
      ),
  },
  'edit-image': {
    component: () => import('@rpgc-ai/lazy-blocks/edit-image/edit-image.component').then((m) => m.EditImageComponent),
  },

  // Model prompt creator — sub-modes passed as `mode` input
  'model-prompt-creator': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/model-prompt-creator/model-prompt-creator.component').then((m) => m.ModelPromptCreatorComponent),
    paramsToInputs: ([subPath] = []) => {
      if (subPath === 'preset') {
        return { preset: true };
      }
      if (subPath === 'author') {
        return { isAuthor: true };
      }
      if (subPath === 'custom') {
        return { isCustom: true };
      }
      return { preset: false };
    },
  },

  // Preset constructor
  [EModalName.PRESET_CONSTRUCTOR]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/modal-preset-constructor/modal-preset-constructor.component').then(
        (m) => m.ModalPresetConstructorComponent,
      ),
    paramsToInputs: ([initiator = EConstructorInitiator.USER]) => ({ initiator }),
  },

  // Prompts / models preview
  [EModalName.PROMPTS_PREVIEW]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/modal-prompts-preview/modal-prompts-preview.component').then((m) => m.ModalPromptsPreviewComponent),
    paramsToInputs: ([id] = []) => (id ? { id } : {}),
  },
  [EModalName.MODELS_PREVIEW]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/modal-models-preview/modal-models-preview.component').then((m) => m.ModalModelsPreviewComponent),
    paramsToInputs: ([modelId] = []) => (modelId ? { modelId } : {}),
  },

  // Chatbot translate

  // Persona / chatbot warnings
  'delete-persona-warning': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/delete-persona-warning/delete-persona-warning.component').then((m) => m.DeletePersonaWarningComponent),
    paramsToInputs: ([id]) => ({ id }),
  },
  'delete-memory-warning': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/memories/delete-memory-warning/delete-memory-warning.component').then(
        (m) => m.DeleteMemoryWarningComponent,
      ),
    paramsToInputs: ([id]) => ({ id }),
  },
  'delete-chat-warning': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/delete-chat-warning/delete-chat-warning.component').then((m) => m.DeleteChatWarningComponent),
    paramsToInputs: ([id]) => ({ id }),
  },
  'delete-chatbot-warning': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/warning-delete-chatbot/warning-delete-chatbot.component').then((m) => m.WarningDeleteChatbotComponent),
    paramsToInputs: (params) => (params.length === 1 ? { botId: params[0] } : { creatorSlug: params[0], botId: params[1] }),
    options: { allowStack: true },
  },

  // Encryption
  'chatbot-encrypted': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/chat-decryption-password/chat-decryption-password.component').then((m) => m.ChatDecryptionComponent),
    options: { suppressHistorySyncOnClose: true },
  },
  'chat-encryption': {
    component: () => import('@rpgc-ai/lazy-blocks/chat-encryption/chat-encryption.component').then((m) => m.ChatEncryptionComponent),
  },

  // QR codes
  'google-play-download': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/qr-code-modal/components/google-play-modal-content/google-play-modal-content.component').then(
        (m) => m.GooglePlayModalComponent,
      ),
  },
  'app-store-download': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/qr-code-modal/components/app-store-modal-content/app-store-modal-content.component').then(
        (m) => m.AppStoreModalComponent,
      ),
  },

  // Share / block / unblock
  'share-chatbot': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/share-bot-wrapper/share-bot-wrapper.component').then((m) => m.ShareBotModalWrapperComponent),
    paramsToInputs: ([link]) => ({ link }),
    options: { title: 'BLOCKS.BLOCK_BUTTON.SHARE' },
  },
  'block-creator': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/warning-block-user/warning-block-user.component').then((m) => m.WarningBlockCreatorComponent),
    paramsToInputs: ([id]) => ({ id }),
    options: { ignoreHistoryBack: true },
  },
  unblock: {
    component: () => import('@rpgc-ai/lazy-blocks/unblock-creator/unblock-creator.component').then((m) => m.UnblockCreatorComponent),
    paramsToInputs: ([id, userName]) => ({ userId: id, userName: userName ?? null }),
  },

  // Placeholder (chatbot creation)
  placeholder: {
    component: () => import('@rpgc-ai/lazy-blocks/placeholder/placeholder-modal.component').then((m) => m.PlaceholderModalComponent),
    paramsToInputs: ([key]) => ({ key }),
  },

  // Profile
  'profile-pic': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/fullscreen-profile-pic/fullscreen-profile-pic.component').then((m) => m.FullscreenProfilePicComponent),
    paramsToInputs: ([id]) => ({ id }),
  },
  'profile-settings': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/profile-settings/profile-settings.component').then((m) => m.ProfileSettingsComponent),
    paramsToInputs: ([initialTab] = []) => ({ initialTab }),
  },
  [EModalName.UNSAVED_CHANGES]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/unsaved-changes-warning/unsaved-changes-warning.component').then(
        (m) => m.UnsavedChangesComponent,
      ),
    options: { allowStack: true },
  },

  // Email verification
  [EModalName.EMAIL_VERIFICATION]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/modal-email-verification/modal-email-verification.component').then((m) => m.EmailVerificationComponent),
    paramsToInputs: ([stage, mandatory]) => ({ stage, mandatory }),
  },
  'email-confirmed': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/email-confirmed/email-confirmed.component').then((m) => m.EmailConfirmedModalComponent),
  },
  'auth-session-error': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/auth-session-error/auth-session-error.component').then((m) => m.AuthSessionErrorModalComponent),
  },

  // Personas
  'choose-persona': {
    component: () => import('@rpgc-ai/lazy-blocks/choose-persona/choose-persona.component').then((m) => m.ChoosePersonaComponent),
  },
  'import-chatbot': {
    component: () => import('@rpgc-ai/lazy-blocks/import-chatbot/import-chatbot.component').then((m) => m.ImportChatbotComponent),
  },
  'confirm-redirect': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/confirm-redirect-modal/confirm-redirect-modal.component').then((m) => m.ConfirmRedirectModalComponent),
    paramsToInputs: ([destination]) => ({ destination }),
  },

  // Chatbot info
  [EModalName.CHATBOT_INFO]: {
    component: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
    paramsToInputs: (params) => ({
      ...(params.length === 1 ? { botId: params[0] } : { creatorSlug: params[0], botId: params[1] }),
      chatbotStatusInput: EChatbotCreateStatus.PREVIEW,
    }),
  },

  // Upgrade / chat query / voiceover
  [EModalName.UPGRADE_SUGGESTION]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/upgrade-suggestion/upgrade-suggestion.component').then((m) => m.UpgradeSuggestionModalComponent),
  },
  [EModalName.CHAT_QUERY]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/modal-chat-query/wait-for-response.component').then((m) => m.ModalWaitForResponseComponent),
  },
  [EModalName.UNLOCK_VOICEOVER]: {
    component: () => import('@rpgc-ai/lazy-blocks/unblock-voiceover/unblock-voiceover.component').then((m) => m.UnblockVoiceoverComponent),
  },

  // Leaderboard
  'leaderboard-history': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/leaderboard-history-modal/leaderboard-history.component').then(
        (m) => m.LeaderboardHistoryComponent,
      ),
    paramsToInputs: ([month, year] = []) => ({
      month: month ? Number(month) : null,
      year: year ? Number(year) : null,
    }),
  },
  'leaderboard-how-it-works': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/leaderboard-how-it-works-modal/leaderboard-how-it-works.component').then(
        (m) => m.LeaderboardHowItWorksComponent,
      ),
  },

  // Markdown
  markdown: {
    component: () => import('@rpgc-ai/lazy-blocks/markdown/markdown.component').then((m) => m.MarkdownComponent),
  },

  // Payments
  payment: {
    component: () => import('@rpgc-ai/lazy-blocks/payment/payment-modal.component').then((m) => m.PaymentModalComponent),
  },
  'payment-result': {
    component: () => import('@rpgc-ai/lazy-blocks/payment-result/payment-result.component').then((m) => m.PaymentResultComponent),
    paramsToInputs: ([id]) => ({ id }),
  },
  'withdrawal-result': {
    component: () => import('@rpgc-ai/lazy-blocks/withdrawal-result/withdrawal-result.component').then((m) => m.WithdrawalResultComponent),
    paramsToInputs: ([id]) => ({ id }),
  },
  'withdrawal-request': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/withdrawal-requests/withdrawal-request-caichat/withdrawal-request-caichat.component').then(
        (m) => m.WithdrawalRequestCaichatComponent,
      ),
  },
  [EModalName.PURCHASE_TOKENS]: {
    component: () => import('@rpgc-ai/lazy-blocks/purchase-modal/purchase-modal.component').then((m) => m.PurchaseModalComponent),
    paramsToInputs: ([packId]) => ({ packId }),
    options: { backdropClass: 'app-purchase-modal-backdrop' },
  },
  [EModalName.PURCHASE_SUBSCRIPTION]: {
    component: () => import('@rpgc-ai/lazy-blocks/purchase-modal/purchase-modal.component').then((m) => m.PurchaseModalComponent),
    paramsToInputs: ([plan, period]) => ({ subscriptionPlan: plan, subscriptionPlanPeriod: period }),
    options: { backdropClass: 'app-purchase-modal-backdrop' },
  },
  [EModalName.PROMO_CODE]: {
    component: () => import('@rpgc-ai/lazy-blocks/modal-promo-code/modal-promo-code.component').then((m) => m.ModalPromoCodeComponent),
  },
  'boosty-payment-status': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/boosty-payment-status/boosty-payment-status.component').then((m) => m.BoostyPaymentStatusComponent),
  },
  'top-up-balance': {
    component: () => import('@rpgc-ai/lazy-blocks/top-up-balance/top-up-balance.component').then((m) => m.TopUpBalanceComponent),
  },
  'top-up-balance-video': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/top-up-balance-video/top-up-balance-video.component').then((m) => m.TopUpBalanceVideoComponent),
  },
  'video-pose-selection': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/video-pose-selection/video-pose-selection.component').then((m) => m.VideoPoseSelectionComponent),
    options: { title: 'PAGES.VIDEO_POSE_SELECTION.TITLE' },
  },

  // Referral campaigns
  'create-referral-campaign': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/create-referral-campaign/create-referral-campaign.component').then(
        (m) => m.CreateReferralCampaignComponent,
      ),
    paramsToInputs: () => ({ mode: 'create' }),
  },
  'edit-referral-campaign': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/create-referral-campaign/create-referral-campaign.component').then(
        (m) => m.CreateReferralCampaignComponent,
      ),
    paramsToInputs: ([campaignId] = []) => ({ mode: 'edit', campaignId: campaignId ?? null }),
  },
  'delete-referral-campaign': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/delete-referral-campaign/delete-referral-campaign.component').then(
        (m) => m.DeleteReferralCampaignComponent,
      ),
    paramsToInputs: ([id] = []) => ({ id: id ?? null }),
  },

  // KYC
  'kyc-verification': {
    component: () => import('@rpgc-ai/lazy-blocks/kyc-verify/kyc-verify.component').then((m) => m.KYCVerifyComponent),
  },

  // Voice
  voice: {
    component: () => import('@rpgc-ai/lazy-blocks/voice-changer/voice-changer.component').then((m) => m.VoiceChangerComponent),
  },

  // Model settings
  'model-settings': {
    component: () => import('@rpgc-ai/lazy-blocks/model-settings/model-settings.component').then((m) => m.ModelSettingsComponent),
    paramsToInputs: ([modelId, promptId]) => ({ modelId, promptId }),
  },

  // Reports / feedback
  'tell-us-more': {
    component: () => import('@rpgc-ai/lazy-blocks/tell-us-more/tell-us-more.component').then((m) => m.TellUsMoreComponent),
    paramsToInputs: ([id, rating]) => ({ id, rating }),
    options: { title: 'MODALS.TELL_US_MORE.TITLE' },
  },
  'report-profile': {
    component: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
    paramsToInputs: ([id]) => ({ id, modalType: EModalType.PROFILE_REPORT }),
  },
  'report-message': {
    component: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
    paramsToInputs: ([id, rating]) => ({ id, rating, modalType: EModalType.MESSAGE_REPORT }),
  },
  'report-chatbot': {
    component: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
    paramsToInputs: ([id]) => ({ id, modalType: EModalType.CHATBOT_REPORT }),
  },

  // Memories
  memories: {
    component: () => import('@rpgc-ai/lazy-blocks/memories/memories.component').then((m) => m.MemoriesComponent),
  },

  // Community guidelines / record keeping / welcome
  [EModalName.GUIDELINES]: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/community-guidelines-agreement/community-guidelines-agreement.component').then(
        (m) => m.CommunityGuidelinesAgreementComponent,
      ),
    options: { disableOverlayClose: true },
  },
  recordKeep: {
    component: () =>
      import('@rpgc-ai/lazy-blocks/record-keeping-container/record-keeping-container.component').then(
        (m) => m.RecordKeepingModalComponent,
      ),
    options: { title: '2257 Record-Keeping Requirements Compliance Statement' },
  },

  'age-confirmation': {
    component: () =>
      import('@rpgc-ai/lazy-blocks/age-confirmation/age-confirmation-modal.component').then((m) => m.AgeConfirmationModalComponent),
    options: {
      disableOverlayClose: true,
      allowStack: true,
      priority: MODAL_PRIORITY.AGE_GATE,
      allowedCloseResults: [AGE_CONFIRMATION_ACCEPTED_RESULT],
      survivesClearStack: true,
    },
  },

  'privacy-policy': {
    component: () => import('@rpgc-ai/pages/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
    options: {
      title: 'MODALS.AGE_CONFIRMATION.PRIVACY_POLICY',
      priority: MODAL_PRIORITY.AGE_LEGAL_OVERLAY,
      allowStack: true,
    },
  },

  'terms-and-conditions': {
    component: () =>
      import('@rpgc-ai/pages/terms-and-conditions/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
    options: {
      title: 'MODALS.AGE_CONFIRMATION.TERMS_AND_CONDITIONS',
      priority: MODAL_PRIORITY.AGE_LEGAL_OVERLAY,
      allowStack: true,
    },
  },
};
